import {
  USER_ACTIVITY_GROUP_LABELS,
  USER_ACTIVITY_INFO,
  userActivityGroup,
  type UserActivityActionCount,
} from '@ai-meeting/shared'
import type { SortDirection } from '@ai-meeting/shared'

/** The columns the activity tab can be ordered by. */
export type ActivitySortBy = 'action' | 'group' | 'total' | 'succeeded' | 'failed' | 'users'

/** What the Area and Function columns print, which is what they sort on. */
function textOf(row: UserActivityActionCount, sortBy: 'action' | 'group'): string {
  return sortBy === 'group'
    ? USER_ACTIVITY_GROUP_LABELS[userActivityGroup(row.action)]
    : USER_ACTIVITY_INFO[row.action].label
}

/**
 * Order the per-function rows for display (BRAI-677).
 *
 * Sorted here rather than by the server, unlike every other tab on this
 * screen: the answer is one row per action in a fixed list, so it arrives
 * whole and paging it back and forth to reorder it would be a round trip to
 * rearrange something already in hand.
 *
 * Both text columns sort on what they print, not on the wire value behind it:
 * the Function column shows "Question typed", and ordering that among the
 * c's because the row holds `copilot.question_asked` would read as broken.
 *
 * Ties break on the action name so the order is stable — without it, the many
 * functions sitting at zero shuffle between renders and the table looks like
 * it is still loading. The wire value is the right tiebreak precisely because
 * it is not on screen: it is unique, where two labels need not be.
 *
 * @param rows The summary as the report returned it; not modified.
 * @returns A new array in the requested order.
 */
export function sortActivityRows(
  rows: UserActivityActionCount[],
  sortBy: ActivitySortBy,
  sortDir: SortDirection
): UserActivityActionCount[] {
  const factor = sortDir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const difference =
      sortBy === 'action' || sortBy === 'group'
        ? textOf(a, sortBy).localeCompare(textOf(b, sortBy)) * factor
        : (a[sortBy] - b[sortBy]) * factor
    return difference !== 0 ? difference : a.action.localeCompare(b.action)
  })
}
